import { useMemo, useRef, useState } from 'react';
import useSWR from 'swr';
import { toast } from 'sonner';
import { Gauge, Mail, Type as TypeIcon, Upload } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { get, post, ApiError } from '@/lib/api';
import { Thumbnail } from './Thumbnail';
import { formatRelative } from '@/lib/time';
import { errMsg, refreshSubmission, consistencyPromptText } from '@/lib/submission-ui';
import { fontNames, type FontsPayload } from './StoreEditorDrawer';
import { mergeAddtextFonts } from '@/lib/submission-zones';
import { AddtextFontSelect } from './AddtextFontSelect';
import { CaptionColorField } from './CaptionColorField';
import { SlotDecorationCard } from './SlotDecorationCard';
import { SlotProposalPanel } from './SlotProposalPanel';
import {
  buildSlotCards, mockupImageUrl, proofViews, readTierSlots, reviewSlotsForTier,
  prizeUsesSameLogo, sameLogoBannerText, tierRollupFromCards, viewLabel,
  flatPreflightStatus, flatPreflightAt,
  type DecorationSlot,
} from '@/lib/submission-slots';
import {
  derivePrintAreas, type DecorationRegistry, type PrintArea, type PrizeProduct,
} from '@/lib/prize-product';
import { tiersFromSubmission, productOfTier, type PrizeTier } from '@/lib/prize-tiers';

export type SubmissionDetail = {
  id: string;
  school_name?: string;
  fundraiserName?: string;
  adminName?: string;
  sportType?: string;
  sport_id?: string;
  expectedUnits?: number | null;
  createdAt?: string;
  underText?: string;
  underTextFont?: string | null;
  underTextColor?: string | null;
  tier1Decoration?: { text?: string; font?: string; textColor?: string } | null;
  tier2Decoration?: { text?: string; font?: string; textColor?: string } | null;
  status?: string;
  tier1Product?: string;
  tier1_product?: string;
  tier1LogoPath?: string | null;
  tier1MockupPath?: string | null;
  tier1PreflightStatus?: string | null;
  tier1PreflightDesignId?: string | number | null;
  tier2Product?: string;
  tier2_product?: string;
  tier2LogoPath?: string | null;
  tier2MockupPath?: string | null;
  tier2PreflightStatus?: string | null;
  tier2PreflightDesignId?: string | number | null;
  // decorationSchemaVersion 2 — one entry per decoration LOCATION on the
  // garment. Absent/version<2 => legacy single-logo submission.
  decorationSchemaVersion?: number | null;
  tier1DecorationSlots?: DecorationSlot[] | null;
  tier2DecorationSlots?: DecorationSlot[] | null;
  // Per-view proof mockups (front/back/sleeve). The legacy tier{N}MockupPath
  // above is the front view only.
  tier1MockupPaths?: Record<string, string> | null;
  tier2MockupPaths?: Record<string, string> | null;
  tier1PreflightAt?: string | null;
  tier2PreflightAt?: string | null;
  tier2UseSameLogo?: boolean | string | null;
  tiers?: PrizeTier[] | null;
  shopifyProductIds?: string[] | null;
  revisions?: Array<{
    revisionNumber?: number;
    requestedAt?: string;
    source?: string;
    notes?: string;
    productChanges?: Array<{ tier: number; from: string; to: string }>;
  }>;
  revisionCount?: number;
  proofEmailSentAt?: string;
  updatedBy?: string | null;
  updatedAt?: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  submission: SubmissionDetail | null | undefined;
  loading?: boolean;
  onEditTier: (tier: number) => void;
};

// Statuses where logo/proof actions make sense (mirrors lib/submission-state +
// lib/proof-state on the server — the server is still the authority).
const EDITABLE = new Set(['pending_worker_review', 'awaiting_rep_decision', 'changes_requested', 'artwork_pending']);
const PROOFABLE = new Set(['pending_worker_review', 'pending_review', 'artwork_pending', 'awaiting_rep_decision', 'proof_sent', 'changes_requested', 'proof_email_failed']);

function PreflightBadge({ status, designId }: { status?: string | null; designId?: string | number | null }) {
  if (status === 'PASS' || status === 'pass') {
    return (
      <Badge className="bg-success/10 text-success">
        ✓ PASS{designId ? ` (design ${designId})` : ''}
      </Badge>
    );
  }
  if (status === 'FAIL' || status === 'fail') {
    return <Badge className="bg-danger/10 text-danger">✗ FAIL</Badge>;
  }
  return <span className="text-[11px] text-muted">— not run</span>;
}

function TierCard({
  submission, tier, product, logoPath, mockupPath, preflightStatus, preflightDesignId, editable, onEdit,
}: {
  submission: SubmissionDetail;
  tier: number;
  product?: string;
  logoPath?: string | null;
  mockupPath?: string | null;
  preflightStatus?: string | null;
  preflightDesignId?: string | number | null;
  editable: boolean;
  onEdit: () => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [textOpen, setTextOpen] = useState(false);
  const storedDeco = (tier === 1 ? submission.tier1Decoration : submission.tier2Decoration) || {};
  const [text, setText] = useState(storedDeco.text || submission.underText || '');
  const [font, setFont] = useState(storedDeco.font || submission.underTextFont || 'zingrustbase');
  const [textColor, setTextColor] = useState(storedDeco.textColor || submission.underTextColor || 'auto');
  const { data: fontsData } = useSWR<FontsPayload>(textOpen ? '/api/fonts' : null, get);
  const fonts = useMemo(() => mergeAddtextFonts(fontNames(fontsData)), [fontsData]);

  async function replaceLogo(file: File) {
    setBusy('replace');
    try {
      const fd = new FormData();
      fd.append('logo', file);
      const res = await fetch(`/api/submissions/${submission.id}/logos/${tier}/replace`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        body: fd,
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new ApiError(res.status, body);
      toast.success(`Tier ${tier} logo replaced — re-preflight runs automatically`);
      await refreshSubmission(submission.id);
    } catch (e) {
      toast.error(errMsg(e, 'Replace failed'));
    } finally {
      setBusy(null);
    }
  }

  async function applyText() {
    if (!text.trim()) return;
    setBusy('text');
    try {
      await post(`/api/submissions/${submission.id}/logos/${tier}/text`, { text: text.trim(), fontName: font, textColor });
      toast.success(`Text added to tier ${tier} logo`);
      setTextOpen(false);
      setText('');
      await refreshSubmission(submission.id);
    } catch (e) {
      toast.error(errMsg(e, 'Add text failed'));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="rounded-[12px] border border-black/[0.06] bg-surface p-3 shadow-card">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-[10.5px] font-semibold uppercase tracking-[0.05em] text-muted">Tier {tier}</span>
        <Button size="sm" variant="secondary" onClick={onEdit}>Edit product</Button>
      </div>
      <div className="flex items-start gap-3">
        <div className="flex shrink-0 gap-1.5">
          {logoPath ? (
            <Thumbnail path={logoPath} alt={`Tier ${tier} logo`} className="h-16 w-16" />
          ) : (
            <div className="flex h-16 w-16 items-center justify-center rounded-[8px] bg-bg text-[10px] text-subtle">no logo</div>
          )}
          {mockupPath && <Thumbnail path={mockupPath} alt={`Tier ${tier} mockup`} className="h-16 w-16" />}
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-[12.5px] font-medium">{product || '(unset)'}</p>
          <p className="mt-1 text-[11px] text-muted">
            Preflight: <PreflightBadge status={preflightStatus} designId={preflightDesignId} />
          </p>
        </div>
      </div>

      {editable && (
        <div className="mt-2.5 flex flex-wrap gap-1.5 border-t border-divider pt-2.5">
          <input
            ref={fileRef} type="file" accept="image/*" className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) void replaceLogo(f); e.target.value = ''; }}
          />
          <Button size="sm" variant="ghost" disabled={!!busy} onClick={() => fileRef.current?.click()}>
            <Upload className="h-3.5 w-3.5" aria-hidden /> {busy === 'replace' ? 'Uploading…' : 'Replace logo'}
          </Button>
          <Button size="sm" variant="ghost" disabled={!!busy} onClick={() => setTextOpen((v) => !v)}>
            <TypeIcon className="h-3.5 w-3.5" aria-hidden /> Add text
          </Button>
        </div>
      )}

      {textOpen && (
        <div className="mt-2 space-y-2 rounded-[10px] bg-bg/60 p-2.5">
          <Input value={text} onChange={(e) => setText(e.target.value)} maxLength={100} placeholder="Text under the logo" />
          <div className="flex flex-wrap items-center gap-2">
            <AddtextFontSelect value={font} onChange={setFont} fonts={fonts} />
            <CaptionColorField value={textColor} onChange={setTextColor} />
            <Button size="sm" disabled={!!busy || !text.trim()} onClick={applyText}>
              {busy === 'text' ? 'Applying…' : 'Apply'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Slot mode (decorationSchemaVersion >= 2)
// ---------------------------------------------------------------------------

function TierRollupBadge({ rollup }: { rollup: ReturnType<typeof tierRollupFromCards> }) {
  if (rollup.status === 'PASS') {
    return <Badge className="bg-success/10 text-success">✓ tier PASS · {rollup.total}/{rollup.total} locations</Badge>;
  }
  if (rollup.status === 'FAIL') {
    return (
      <Badge className="bg-danger/10 text-danger">
        ✗ tier FAIL · {rollup.passed}/{rollup.total} locations
        {rollup.failedSlotIds.length
          ? ` · blocked by ${rollup.failedSlotIds.join(', ')}`
          : ` · tier status ${rollup.flatStatus || 'unset'}`}
      </Badge>
    );
  }
  return <Badge className="bg-bg text-muted">preflight not run · {rollup.total} locations</Badge>;
}

// The per-view proof mockups. Before slot mode a tier had exactly one (front);
// a back-name/number tier is only fully proofed when the back view is there too.
function ProofMockups({ submission, tier }: { submission: SubmissionDetail; tier: number }) {
  const views = proofViews(submission as unknown as Record<string, unknown>, tier);
  if (!views.length) {
    return <p className="mt-2 text-[10.5px] text-muted">No proof mockups yet — generate the proof below.</p>;
  }
  return (
    <div className="mt-2">
      <p className="text-[10px] font-medium uppercase tracking-[0.05em] text-muted">Proof mockups</p>
      <div className="mt-1 flex flex-wrap gap-2">
        {views.map((v) => (
          <a
            key={v}
            href={mockupImageUrl(submission.id, tier, v)}
            target="_blank"
            rel="noreferrer"
            className="flex flex-col items-center gap-0.5"
            title={`Open the ${viewLabel(v).toLowerCase()} proof`}
          >
            <img
              src={mockupImageUrl(submission.id, tier, v)}
              alt={`Tier ${tier} ${viewLabel(v)} proof`}
              className="h-16 w-16 rounded-[8px] bg-bg object-contain"
              loading="lazy"
            />
            <span className="text-[10px] text-muted">{viewLabel(v)}</span>
          </a>
        ))}
      </div>
    </div>
  );
}

function SlotTierCard({
  submission, tier, product, slots, editable, onEdit,
}: {
  submission: SubmissionDetail;
  tier: number;
  product?: string;
  slots: DecorationSlot[];
  editable: boolean;
  onEdit: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const { data: registry, error: registryError } = useSWR<DecorationRegistry>('/api/decoration-locations', get);
  const { data: productDoc, error: productError } = useSWR<PrizeProduct>(
    product ? `/api/prize-products/${encodeURIComponent(product)}` : null, get,
  );

  // derivePrintAreas() throws exactly where the server throws (unknown
  // placement, a view with no base image). Surface the message — a silent
  // catch would show every location as "no print area".
  let printAreas: Record<string, PrintArea> | undefined;
  let printAreaError: string | null = null;
  if (productDoc && registry) {
    try {
      printAreas = derivePrintAreas(productDoc, registry).printAreas;
    } catch (e) {
      printAreaError = e instanceof Error ? e.message : String(e);
    }
  }

  const cards = buildSlotCards(slots, registry, printAreas);
  // The flat preflight status is half the approval gate (approveproof
  // reads it as well as the slots), so the badge must see it too — otherwise
  // the drawer can show PASS on a tier approveproof will refuse. T3+ live on
  // tiers[], not invented tier3PreflightStatus fields.
  const rollup = tierRollupFromCards(
    cards,
    flatPreflightStatus(submission, tier),
  );
  const logoCount = rollup.logoCount;
  // Any change to artwork or preflight must re-fetch the images, not the cache.
  const cacheKey = [
    submission.updatedAt || '',
    flatPreflightAt(submission, tier) || '',
    slots.map((s) => s?.logoPath || '').join('|'),
  ].join('~');

  async function runPreflight() {
    setBusy(true);
    try {
      const r = await post<{ slotCount: number | null; passedSlotCount: number | null; passed: boolean; failedSlotIds: string[] }>(
        `/api/submissions/${submission.id}/preflight/${tier}`, {},
      );
      if (r.slotCount) {
        toast[r.passed ? 'success' : 'error'](
          `Tier ${tier}: ${r.passedSlotCount}/${r.slotCount} locations pass` +
          (r.failedSlotIds?.length ? ` — ${r.failedSlotIds.join(', ')} still blocking` : ''),
        );
      } else {
        toast[r.passed ? 'success' : 'error'](`Tier ${tier} preflight ${r.passed ? 'passed' : 'failed'}`);
      }
      await refreshSubmission(submission.id);
    } catch (e) {
      toast.error(errMsg(e, 'Preflight failed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="rounded-[12px] border border-black/[0.06] bg-surface p-3 shadow-card"
      data-prize-tier={tier}
      {...(tier > 2 ? { 'data-extra-tier': tier } : {})}
    >
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="text-[10.5px] font-semibold uppercase tracking-[0.05em] text-muted">
          Tier {tier} · {cards.length} decoration{cards.length === 1 ? '' : 's'}
        </span>
        <Button size="sm" variant="secondary" onClick={onEdit}>Edit product</Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <p className="min-w-0 flex-1 truncate text-[12.5px] font-medium">{product || '(unset)'}</p>
        <TierRollupBadge rollup={rollup} />
      </div>

      {registryError && (
        <p className="mt-1 text-[10.5px] text-danger">
          Decoration locations unavailable — locations cannot be labelled or sized. ({errMsg(registryError, 'registry error')})
        </p>
      )}
      {productError && (
        <p className="mt-1 text-[10.5px] text-danger">
          Product “{product}” could not be loaded — print sizes are unknown. ({errMsg(productError, 'product error')})
        </p>
      )}
      {printAreaError && (
        <p className="mt-1 text-[10.5px] text-danger">Print areas unresolved: {printAreaError}</p>
      )}
      {rollup.blockedReason && (
        <p className="mt-1 text-[10.5px] text-danger">{rollup.blockedReason}</p>
      )}

      <div className="mt-2 space-y-2">
        {cards.map((card) => (
          <SlotDecorationCard
            key={card.slotId}
            submissionId={submission.id}
            tier={tier}
            card={card}
            editable={editable}
            cacheKey={cacheKey}
          />
        ))}
      </div>

      <div className="mt-2.5 flex items-center justify-between gap-2 border-t border-divider pt-2.5">
        <p className="text-[10.5px] text-muted">
          One run covers every location on this tier — the engine preflights all {logoCount} logo
          location{logoCount === 1 ? '' : 's'} and writes all {cards.length} badges together.
        </p>
        <Button
          size="sm"
          variant="secondary"
          disabled={busy}
          data-run-preflight={tier}
          onClick={runPreflight}
        >
          <Gauge className="h-3.5 w-3.5" aria-hidden />
          {busy ? 'Running…' : `Run preflight · all ${cards.length} locations`}
        </Button>
      </div>

      <ProofMockups submission={submission} tier={tier} />
    </div>
  );
}

// A tier renders EXACTLY the pre-slot-mode TierCard unless the submission
// carries decoration slots. That branch is the regression lock for every
// legacy submission in the system.
function TierSection({
  submission, tier, product, mockupPath, preflightStatus, preflightDesignId, editable, onEdit,
}: {
  submission: SubmissionDetail;
  tier: number;
  product?: string;
  mockupPath?: string | null;
  preflightStatus?: string | null;
  preflightDesignId?: string | number | null;
  editable: boolean;
  onEdit: () => void;
}) {
  const slots = readTierSlots(submission, tier);
  if (slots) {
    return (
      <SlotTierCard
        submission={submission}
        tier={tier}
        product={product}
        slots={slots}
        editable={editable}
        onEdit={onEdit}
      />
    );
  }
  return (
    <TierCard
      submission={submission}
      tier={tier}
      product={product}
      logoPath={tier === 1 ? submission.tier1LogoPath : submission.tier2LogoPath}
      mockupPath={mockupPath}
      preflightStatus={preflightStatus}
      preflightDesignId={preflightDesignId}
      editable={editable}
      onEdit={onEdit}
    />
  );
}

function ExtraProductCard({
  submission, tier, onEdit, editable,
}: {
  submission: SubmissionDetail;
  tier: PrizeTier;
  onEdit: () => void;
  editable: boolean;
}) {
  // Same SlotTierCard T1/T2 use — Run preflight POSTs /preflight/:tier which
  // already accepts 1..MAX_PRIZE_TIERS and reads currentProduct from tiers[].
  // A T1-mirror (useSameLogo) lists T1 placements on THIS card. Do not fetch
  // T3 slot images or invent a third regen bench — applying T1 stamps T3.
  const product = productOfTier(submission as unknown as Record<string, unknown>, tier.index);
  const review = reviewSlotsForTier(submission, tier.index);
  if (prizeUsesSameLogo(submission, tier.index)) {
    const mirrored = review.slots || [];
    const logoPath = String(tier.logoPath || submission.tier1LogoPath || '');
    return (
      <div
        className="rounded-[12px] border border-black/[0.06] bg-surface p-3 shadow-card"
        data-extra-tier={tier.index}
        data-same-logo-tier={tier.index}
      >
        <div className="mb-2 flex items-center justify-between">
          <span className="text-[10.5px] font-semibold uppercase tracking-[0.05em] text-muted">
            Product {tier.index}
          </span>
          {editable && <Button size="sm" variant="secondary" onClick={onEdit}>Edit product</Button>}
        </div>
        <p className="mb-2 rounded-[8px] border border-blue-200 bg-blue-50 px-2.5 py-1.5 text-[11.5px] text-blue-800">
          {sameLogoBannerText(submission, tier.index)}
        </p>
        <div className="flex items-start gap-3">
          {logoPath ? (
            <Thumbnail path={logoPath} alt={`Product ${tier.index} logo`} className="h-16 w-16" />
          ) : (
            <div className="flex h-16 w-16 items-center justify-center rounded-[8px] bg-bg text-[10px] text-subtle">no logo</div>
          )}
          <div className="min-w-0 flex-1">
            <p className="truncate text-[12.5px] font-medium">{product || '(unset)'}</p>
            {mirrored.length ? (
              <ul className="mt-1 space-y-0.5 text-[11px] text-muted">
                {mirrored.map((slot, i) => (
                  <li key={slot.slotId || `m${i}`}>
                    {slot.code || 'Unknown'}
                    {i === 0 ? ' · PRIMARY' : ''}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-1 text-[10.5px] text-muted">Mirrors T1 primary logo.</p>
            )}
          </div>
        </div>
      </div>
    );
  }
  const slots = readTierSlots(submission, tier.index);
  if (slots) {
    return (
      <SlotTierCard
        submission={submission}
        tier={tier.index}
        product={product}
        slots={slots}
        editable={editable}
        onEdit={onEdit}
      />
    );
  }
  const logoPath = String(tier.logoPath || '');
  return (
    <div className="rounded-[12px] border border-black/[0.06] bg-surface p-3 shadow-card" data-extra-tier={tier.index}>
      <div className="mb-2 flex items-center justify-between">
        <span className="text-[10.5px] font-semibold uppercase tracking-[0.05em] text-muted">
          Product {tier.index}
        </span>
        {editable && <Button size="sm" variant="secondary" onClick={onEdit}>Edit product</Button>}
      </div>
      <div className="flex items-start gap-3">
        {logoPath ? (
          <Thumbnail path={logoPath} alt={`Product ${tier.index} logo`} className="h-16 w-16" />
        ) : (
          <div className="flex h-16 w-16 items-center justify-center rounded-[8px] bg-bg text-[10px] text-subtle">no logo</div>
        )}
        <div className="min-w-0 flex-1">
          <p className="truncate text-[12.5px] font-medium">{product || '(unset)'}</p>
          <p className="mt-1 text-[10.5px] text-muted">Included in Send Proof, Approve, and Place Order.</p>
        </div>
      </div>
    </div>
  );
}

export function SubmissionWorkerDrawer({ open, onClose, submission, loading, onEditTier }: Props) {
  const [proofBusy, setProofBusy] = useState(false);
  const editable = !!submission && EDITABLE.has(submission.status || '');
  const proofable = !!submission && PROOFABLE.has(submission.status || '');
  const isResend = submission?.status === 'awaiting_rep_decision' || submission?.status === 'proof_sent';
  const cancelled = submission?.status === 'cancelled';

  async function generateProof() {
    if (!submission) return;
    if (!confirm(`${isResend ? 'Re-send' : 'Generate and send'} the proof email for ${submission.school_name || submission.id}?`)) return;
    setProofBusy(true);
    const send = (body: Record<string, unknown>) =>
      post<{ message?: string }>(`/api/submissions/${submission.id}/generate-proof`, body);
    try {
      const r = await send({});
      toast.success(r.message || 'Proof generated and emailed');
      await refreshSubmission(submission.id);
    } catch (e) {
      const body = e instanceof ApiError ? (e.body as { code?: string; findings?: Array<{ detail?: string; id?: string }> } | null) : null;
      if (e instanceof ApiError && e.status === 409 && body?.code === 'PROOF_CONSISTENCY_FAILED') {
        const reason = window.prompt(consistencyPromptText(body.findings));
        if (reason === null) {
          toast.error('Not sent — fix the listed problems first');
        } else if (reason.trim().length < 3) {
          toast.error('Reason must be at least 3 characters — not sent');
        } else {
          try {
            const r = await send({ consistencyOverride: { reason: reason.trim() } });
            toast.success(r.message || 'Proof sent (override recorded)');
            await refreshSubmission(submission.id);
          } catch (again) {
            toast.error(errMsg(again, 'Proof generation failed'));
          }
        }
      } else {
        toast.error(errMsg(e, 'Proof generation failed'));
      }
    } finally { setProofBusy(false); }
  }

  async function reopenFundraiser() {
    if (!submission) return;
    if (!confirm(`Reopen ${submission.fundraiserName || submission.school_name || submission.id}?\n\nThis will send a new proof. Old approve/cancel links stay invalid.`)) return;
    setProofBusy(true);
    try {
      const r = await post<{ message?: string }>(`/api/submissions/${submission.id}/reopen`, {});
      toast.success(r.message || 'Reopened — new proof sent. Old links stay invalid.');
      await refreshSubmission(submission.id);
    } catch (e) {
      const body = e instanceof ApiError ? (e.body as { code?: string } | null) : null;
      if (e instanceof ApiError && e.status === 409 && body?.code === 'PROOF_CONSISTENCY_FAILED') {
        toast.error('Reopened — but the proof needs attention: use Generate & send to review the problems or send anyway');
      } else {
        toast.error(errMsg(e, 'Reopen failed'));
      }
      // approveproof already reopened the fundraiser by the time generateproof
      // can refuse the proof — keep the drawer in sync either way.
      await refreshSubmission(submission.id);
    } finally {
      setProofBusy(false);
    }
  }

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-[480px] overflow-y-auto sm:max-w-[480px]">
        <SheetHeader>
          <SheetTitle>{submission?.school_name || submission?.fundraiserName || 'Submission'}</SheetTitle>
          {submission && (
            <div className="space-y-1 pt-0.5">
              <div className="flex flex-wrap items-center gap-2">
                {submission.status && <Badge className="bg-bg text-text">{submission.status}</Badge>}
                {submission.proofEmailSentAt && (
                  <span className="text-[11px] text-muted">proof sent {formatRelative(submission.proofEmailSentAt)}</span>
                )}
              </div>
              <p className="text-[11px] text-muted">
                {[
                  submission.fundraiserName && submission.fundraiserName !== submission.school_name ? submission.fundraiserName : null,
                  submission.sportType || submission.sport_id,
                  submission.adminName ? `admin ${submission.adminName}` : null,
                  submission.createdAt ? `created ${formatRelative(submission.createdAt)}` : null,
                ].filter(Boolean).join(' · ')}
              </p>
              <p className="font-mono text-[10px] text-subtle">{submission.id}</p>
            </div>
          )}
        </SheetHeader>

        {loading ? (
          <div className="mt-6 space-y-3">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        ) : submission ? (
          <div className="mt-5 space-y-3">
            {/* Decoration changes the customer requested from their proof email
                and that NOBODY has applied yet. Renders nothing when there are
                none; above the tier cards when there are, because an unapplied
                request means the customer is waiting on us. */}
            <SlotProposalPanel submissionId={submission.id} />
            {tiersFromSubmission(submission as unknown as Record<string, unknown>).map((t) => (
              t.index <= 2 ? (
                <TierSection
                  key={t.index}
                  submission={submission}
                  tier={t.index}
                  product={productOfTier(submission as unknown as Record<string, unknown>, t.index)}
                  mockupPath={String(t.mockupPath || (t.index === 1 ? submission.tier1MockupPath : submission.tier2MockupPath) || '') || null}
                  preflightStatus={(t.preflightStatus || (t.index === 1 ? submission.tier1PreflightStatus : submission.tier2PreflightStatus) || null) as string | null}
                  preflightDesignId={(t.preflightDesignId ?? (t.index === 1 ? submission.tier1PreflightDesignId : submission.tier2PreflightDesignId) ?? null) as string | number | null}
                  editable={editable}
                  onEdit={() => onEditTier(t.index)}
                />
              ) : (
                <ExtraProductCard
                  key={t.index}
                  submission={submission}
                  tier={t}
                  editable={editable}
                  onEdit={() => onEditTier(t.index)}
                />
              )
            ))}

            {Array.isArray(submission.revisions) && submission.revisions.length > 0 && (
              <details className="rounded-[12px] border border-black/[0.06] bg-surface p-3 text-[11px] shadow-card">
                <summary className="cursor-pointer text-[11.5px] font-medium">
                  Revisions ({submission.revisionCount ?? submission.revisions.length})
                </summary>
                <ul className="mt-2 space-y-1.5">
                  {submission.revisions.slice(-5).reverse().map((r, i) => (
                    <li key={i} className="text-muted">
                      <span className="font-mono text-[10px]">#{r.revisionNumber ?? '?'}</span>
                      {' · '}
                      <span>{r.requestedAt ? formatRelative(r.requestedAt) : '?'}</span>
                      {r.source && <span> · <code className="text-[10px]">{r.source}</code></span>}
                      {r.productChanges?.map((pc) => (
                        <span key={pc.tier}> · tier{pc.tier}: {pc.from} → {pc.to}</span>
                      ))}
                    </li>
                  ))}
                </ul>
              </details>
            )}

            {cancelled && (
              <div className="rounded-[12px] border border-black/[0.06] bg-surface p-3 shadow-card">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-[12px] font-medium">Reopen fundraiser</p>
                    <p className="text-[10.5px] text-muted">This will send a new proof. Old approve/cancel links stay invalid.</p>
                  </div>
                  <Button disabled={proofBusy} onClick={reopenFundraiser}>
                    <Mail className="h-4 w-4" aria-hidden /> {proofBusy ? 'Reopening…' : 'Reopen fundraiser'}
                  </Button>
                </div>
              </div>
            )}

            {proofable && (
              <div className="rounded-[12px] border border-black/[0.06] bg-surface p-3 shadow-card">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-[12px] font-medium">{isResend ? 'Re-send proof' : 'Generate proof'}</p>
                    <p className="text-[10.5px] text-muted">Builds tier mockups and emails the approval link to the customer + rep.</p>
                  </div>
                  <Button disabled={proofBusy} onClick={generateProof}>
                    <Mail className="h-4 w-4" aria-hidden /> {proofBusy ? 'Sending… (can take a minute)' : isResend ? 'Re-send' : 'Generate & send'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
